/**
 * Handling / shipper / consignee line shared by feed booking drill-downs.
 */
import { SensitiveText } from "@/components/common/Sensitive";
import { formatDimensionValue } from "@/lib/formatDimensionValue";
import { formatPersonName } from "@/lib/formatPersonName";
import { cn } from "@/lib/utils";

function PartyBit({
  label,
  value,
  format,
}: {
  label: string;
  value?: string;
  format: "person" | "consignee";
}) {
  if (!value) return null;
  const display =
    format === "consignee"
      ? formatDimensionValue("consignee", value)
      : formatPersonName(value);
  return (
    <span className="inline-flex min-w-0 max-w-full items-baseline gap-1">
      <span className="shrink-0 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground/80">
        {label}
      </span>
      <span className="min-w-0 truncate">
        <SensitiveText value={display} />
      </span>
    </span>
  );
}

export function FeedBookingDrillPartyLine({
  agent,
  shipper,
  consignee,
  className,
  variant = "inline",
  /** Hide handling agent (often already on the card scope). */
  omitAgent = false,
}: {
  agent?: string;
  shipper?: string;
  /** End-client code (`bookings.consignee`), not `consignee_name`. */
  consignee?: string;
  className?: string;
  variant?: "inline" | "summary";
  omitAgent?: boolean;
}) {
  const hasParties = Boolean(
    (!omitAgent && agent) || shipper || consignee,
  );
  if (!hasParties) return null;

  if (variant === "summary") {
    const segments: Array<{
      key: string;
      label: string;
      value: string;
      format: "person" | "consignee";
    }> = [];
    if (!omitAgent && agent?.trim()) {
      segments.push({
        key: "agent",
        label: "Handling",
        value: agent,
        format: "person",
      });
    }
    if (shipper?.trim()) {
      segments.push({
        key: "shipper",
        label: "Shipper",
        value: shipper,
        format: "person",
      });
    }
    if (consignee?.trim()) {
      segments.push({
        key: "consignee",
        label: "Consignee",
        value: consignee,
        format: "consignee",
      });
    }
    if (segments.length === 0) return null;

    return (
      <p
        className={cn(
          "mt-1 min-w-0 truncate text-[11px] leading-snug text-muted-foreground",
          className,
        )}
      >
        {segments.map((segment, index) => {
          const display =
            segment.format === "consignee"
              ? formatDimensionValue("consignee", segment.value)
              : formatPersonName(segment.value);
          return (
            <span key={segment.key}>
              {index > 0 && (
                <span className="px-1 text-muted-foreground/45" aria-hidden>
                  ·
                </span>
              )}
              <span className="text-muted-foreground/75">{segment.label}</span>
              <span className="text-muted-foreground/45"> · </span>
              <SensitiveText value={display} />
            </span>
          );
        })}
      </p>
    );
  }

  return (
    <div
      className={
        className ??
        "mt-1 flex min-w-0 flex-wrap items-baseline gap-x-2.5 gap-y-0.5 text-[10px] text-muted-foreground"
      }
    >
      {!omitAgent && <PartyBit label="Handling" value={agent} format="person" />}
      <PartyBit label="Shipper" value={shipper} format="person" />
      <PartyBit label="Consignee" value={consignee} format="consignee" />
    </div>
  );
}
